import { cn, cv, NcTooltip, NcTooltipTriggerProps } from "@noted/noted-components";
import { ComponentProps, FC, SVGProps } from "react";
import { NavLink as ReactRouterNavLink } from "react-router-dom";

const navLinkVariants = cv(
  cn(
    "flex gap-3 items-center p-4 pl-6 min-h-6 text-light transition-colors duration-150 ease-in-out",
    "focus-visible:underline focus-visible:outline-light focus-visible:outline-2 -outline-offset-2"
  ),
  {
    variants: {
      isActive: {
        true: "cursor-default font-bold bg-main-dark relative before:absolute before:top-1 before:bottom-1 before:left-0 before:w-1 before:bg-active before:rounded-r-full",
        false: "hover:bg-brand-active focus-visible:bg-brand-active",
      },
    },
  }
);

export interface NavLinkProps extends ComponentProps<typeof ReactRouterNavLink> {
  title: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  desktopExpanded?: boolean;
}

interface NavLinkTooltipProps extends NcTooltipTriggerProps {
  title: string;
}

const NavLinkTooltip = ({ title, children, delay = 750, ...props }: NavLinkTooltipProps) => (
  <NcTooltip.Trigger delay={delay} {...props}>
    {children}
    <NcTooltip placement="right">{title}</NcTooltip>
  </NcTooltip.Trigger>
);

/** Using React router Link to get active route styling
 *  This must be wrapped in a Focusable component for using with tooltips */
export const NavLink = ({ title, icon: Icon, desktopExpanded, ...props }: NavLinkProps) => {
  return (
    <NavLinkTooltip title={title} isDisabled={desktopExpanded}>
      <NcTooltip.Focusable>
        <ReactRouterNavLink className={({ isActive }) => navLinkVariants({ isActive })} {...props}>
          <Icon className="max-h-6 max-w-6 shrink-0" />
          <span className={cn("truncate", !desktopExpanded && "lg:hidden")}>{title}</span>
        </ReactRouterNavLink>
      </NcTooltip.Focusable>
    </NavLinkTooltip>
  );
};

interface NavLinkAnchorProps {
  title: string;
  icon: FC<SVGProps<SVGSVGElement>>;
  desktopExpanded?: boolean;
  href: string;
  target?: string;
  role?: string;
  onClick?: () => void;
}

/** Using vanilla anchor for external links as no need for NcLink
 *  This must be wrapped in a Focusable component for using with tooltips */
export const NavLinkAnchor = ({
  icon: Icon,
  title,
  desktopExpanded,
  ...props
}: NavLinkAnchorProps) => {
  return (
    <NavLinkTooltip title={title} isDisabled={desktopExpanded}>
      <NcTooltip.Focusable>
        <a className={navLinkVariants({ isActive: false })} {...props}>
          <Icon className="max-h-6 max-w-6 shrink-0" />
          <span className={cn("truncate", !desktopExpanded && "sr-only")}>{title}</span>
        </a>
      </NcTooltip.Focusable>
    </NavLinkTooltip>
  );
};
